import { useEffect, useState } from 'react';
import { FormCheck } from 'react-bootstrap';

import { Trade } from '../../types/Quality/Trade';

type SelectableTrade = {
    selected: boolean,
    trade: Trade,
};

type BenchmarkTradeDisplayProps = {
    trades: Trade[],
    allTrades?: Trade[],
    editable?: boolean,
    listOnly?: boolean,
    onTradesChange?: (_: Trade[]) => void
};

/**
 * These are to prevent re-entrance of the useEffect
 *   Because of the way the default props are called out re-entrance would
 *   occur (every instance would create a new tradelist [])
 */
const EMPTY_TRADES: Trade[] = [];
const NOOP = (_: Trade[]) => {};

const BenchmarkTradeDisplay = ({
    trades,
    allTrades = EMPTY_TRADES,
    editable = false,
    listOnly = true,
    onTradesChange = NOOP,
}: BenchmarkTradeDisplayProps) => {
    const [selectedTrades, setSelectedTrades] = useState<SelectableTrade[]>([]);

    /**
     * Build the selectedTrades List with the trades that the user has selected
     *   it will also help with re-rendering the component as the user selects
     *   and deselects trades from the list
     */
    useEffect(() => {
        const selectedTrades = new Set(trades.map(t => t.id)); // subset for lookup
        setSelectedTrades(allTrades.map((t) => ({
            selected: selectedTrades.has(t.id),
            trade: t
        })));
    }, [trades, allTrades, setSelectedTrades]);

    /**
     * 
     * @param index; index of the trade in the list of selectedTrades
     */
    const handleTradeToggle = (index: number) => {
        const newSelectedTrades = [...selectedTrades];
        newSelectedTrades[index].selected = !selectedTrades[index].selected;
        onTradesChange(newSelectedTrades.filter(s => s.selected).map(s => s.trade));
        setSelectedTrades(newSelectedTrades);
    };

    /**
     * This will catch if we only want to display the list and will send
     *   back a list without checkboxes
     */
    if (listOnly) {
        if (!trades || trades.length === 0) {
            return(<>NO TRADES LINKED</>);
        }
        return(
            <div className='row p-1'>
                <div className='col' style={{maxHeight: '90px', overflow: 'auto'}} >
                    {trades.map((trade) => (
                        <div key={trade.id} >
                            {trade.name}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    /**
     * This will catch if we only want be able to edit the list and will send
     *   back a list checkboxes and handle any updates to the list
     */
    return(
        <div className='row p-1'>
            <div
                className='col'
                style={{maxHeight: '200px', overflow: 'auto'}}
            >
                {selectedTrades.map(({selected, trade }, i) => (
                    <FormCheck
                        key={trade.id}
                        type='checkbox'
                        id={`trade-${trade.id}`}
                        label={trade.name}
                        checked={selected}
                        onChange={() => handleTradeToggle(i)}
                        disabled={!editable}
                    />
                ))}
            </div>
        </div>
    );
};

export default BenchmarkTradeDisplay;